import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useCollaboration } from '../hooks/useCollaboration';

const CollaborationContext = createContext(null);

// ✅ TON PROVIDER ORIGINAL - INCHANGÉ
export const CollaborationProvider = ({ children, userId, userName, userRole, token }) => {
  const collaboration = useCollaboration({ userId, userName, userRole, token });

  return (
    <CollaborationContext.Provider value={collaboration}>
      {children}
    </CollaborationContext.Provider>
  );
};

export const useCollaborationContext = () => {
  const context = useContext(CollaborationContext);
  if (!context) {
    throw new Error('useCollaborationContext must be used within a CollaborationProvider');
  }
  return context;
};

// ✅ TON PROTECTEDMODAL ORIGINAL - AVEC GESTION show
export const ProtectedModal = ({
  children,
  show = true,  // ✅ Ajout de la prop show avec valeur par défaut
  modalType,
  modalId,
  onLockAcquired,
  onLockDenied,
  onClose,
  title,
  lockKey,
  style = {},
  className = ''
}) => {
  // Utiliser lockKey si fourni, sinon construire à partir de modalType-modalId
  const effectiveLockKey = lockKey || `${modalType}-${modalId}`;
  const effectiveParts = effectiveLockKey.split('-');
  const effectiveModalType = effectiveParts[0] || 'modal';
  const effectiveModalId = effectiveParts.slice(1).join('-') || 'default';

  const {
    requestModalLock,
    releaseModalLock,
    getTypingUsers,
    isConnected,
    modalLocks
  } = useCollaborationContext();

  const [isLocked, setIsLocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lockDeniedMessage, setLockDeniedMessage] = useState('');
  const [lockRequested, setLockRequested] = useState(false);
  const timeoutRef = useRef(null);

  // ✅ Reset quand show change
  useEffect(() => {
    if (!show) {
      setLockRequested(false);
      setIsLoading(true);
      setIsLocked(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [show]);

  // ✅ OBSERVER modalLocks directement
  useEffect(() => {
    if (!show) return;

    const hasLock = modalLocks.has(effectiveLockKey);

    if (lockRequested && hasLock && isLoading) {
      console.log(`✅ Verrou détecté dans modalLocks: ${effectiveLockKey}`);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        console.log(`⏰ Timeout annulé pour: ${effectiveLockKey}`);
      }

      setIsLocked(true);
      setIsLoading(false);
      onLockAcquired && onLockAcquired();
    }
  }, [show, modalLocks, effectiveLockKey, lockRequested, isLoading, onLockAcquired]);

  // ✅ Demander le verrou une seule fois
  useEffect(() => {
    if (!show) return;

    if (isConnected && !lockRequested) {
      console.log(`🔍 Première demande verrou: ${effectiveLockKey}`);
      setLockRequested(true);
      setIsLoading(true);
      requestModalLock(effectiveModalType, effectiveModalId);

      timeoutRef.current = setTimeout(() => {
        const hasLock = modalLocks.has(effectiveLockKey);

        if (!hasLock && isLoading) {
          console.log(`⏰ Timeout - verrou refusé: ${effectiveLockKey}`);
          setIsLoading(false);
          setIsLocked(false);
          setLockDeniedMessage('Élément en cours de modification par un autre utilisateur');
          onLockDenied && onLockDenied();
        }
        timeoutRef.current = null;
      }, 3000);
    }
  }, [show, isConnected, effectiveLockKey, effectiveModalType, effectiveModalId, lockRequested, requestModalLock, modalLocks, isLoading, onLockDenied]);

  // ✅ Libération propre
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (isLocked) {
        console.log(`🔓 Libération verrou: ${effectiveLockKey}`);
        releaseModalLock(effectiveModalType, effectiveModalId);
      }
    };
  }, [isLocked, effectiveLockKey, effectiveModalType, effectiveModalId, releaseModalLock]);

  const typingUser = getTypingUsers(effectiveModalType, effectiveModalId);

  // ✅ Si show est false, ne rien afficher (APRÈS les hooks)
  if (!show) {
    return null;
  }

  // ID unique pour l'accessibilité
  const modalTitleId = `modal-title-${effectiveLockKey}`;

  // ✅ Wrapper modale pour les états de chargement et erreur
  const ModalWrapper = ({ children: content }) => (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        maxWidth: '400px',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {content}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <ModalWrapper>
        <h3 id={modalTitleId} className="sr-only">Chargement en cours</h3>
        <div
          role="progressbar"
          aria-label="Vérification des accès"
          style={{
            border: '3px solid #007bff',
            borderTop: '3px solid transparent',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}
        ></div>
        <span style={{ fontSize: '16px', color: '#666' }} aria-live="polite">Vérification des accès...</span>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </ModalWrapper>
    );
  }

  if (!isLocked) {
    return (
      <ModalWrapper>
        <div style={{ fontSize: '48px', marginBottom: '16px' }} aria-hidden="true">🔒</div>
        <h3
          id={modalTitleId}
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#343a40',
            marginBottom: '12px'
          }}
        >
          Élément en cours de modification
        </h3>
        <p style={{ color: '#6c757d', margin: '0 0 20px' }}>
          {lockDeniedMessage}
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Fermer
            </button>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🔄 Réessayer
          </button>
        </div>
      </ModalWrapper>
    );
  }

  // ✅ Rendu en vraie modale avec overlay
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? modalTitleId : undefined}
      aria-label={!title ? `Modal ${effectiveModalType}` : undefined}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'clamp(10px, 3vw, 20px)',
        ...style
      }}
      className={className}
    >
      <div style={{
        background: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: 'min(98vw, 1400px)',
        maxHeight: '95vh',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* En-tête de la modale */}
        {(title || onClose) && (
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e1e5e9',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#f8f9fa'
          }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>
              {title || 'Formulaire'}
            </h2>
            {onClose && (
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0 8px',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            )}
          </div>
        )}

        {/* Indicateur de frappe */}
        {typingUser && (
          <div style={{
            background: '#e3f2fd',
            border: '1px solid #90caf9',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '14px',
            color: '#1565c0'
          }}>
            <span>👤 {typingUser.userName} est en train de modifier...</span>
          </div>
        )}

        {/* Contenu de la modale */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
          {children}
        </div>
      </div>
    </div>
  );
};